import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, Phone, MapPin, ChevronDown } from "lucide-react";
import { classes } from "./tokens";
import { trackEvent } from "../../utils/gtag";
import { buildCarServiceWhatsAppHref, PHONE_NUMBER, PHONE_NUMBER_DISPLAY } from "./whatsappConfig";
import { useCarServiceCity } from "./CarServiceCityContext";

/**
 * Minimal LP header — logo left, city dropdown + WhatsApp + Call CTAs right.
 * Zero other nav links. Every off-page link is a paid-ads leak.
 *
 * The city dropdown is data-driven — its list is fetched from
 * `/api/public/carservice/cities` via CarServiceCityContext. NEVER hardcode
 * the list here; new cities the ops team turns on must appear
 * automatically.
 */
const CarServiceHeader = () => {
  const telHref = `tel:${PHONE_NUMBER}`;
  const { cities, loading, selectedCity, setSelectedCity } = useCarServiceCity();
  const whatsappHref = buildCarServiceWhatsAppHref({ city: selectedCity, clickType: "header" });
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Close on Escape + arrow-key navigation for a11y.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % Math.max(cities.length, 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + cities.length) % Math.max(cities.length, 1));
      } else if (e.key === "Enter" && activeIdx >= 0 && cities[activeIdx]) {
        e.preventDefault();
        setSelectedCity(cities[activeIdx]);
        setOpen(false);
        triggerRef.current?.focus();
        trackEvent("city_selected", { city: cities[activeIdx].name, source: "header_keyboard" });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, cities, activeIdx, setSelectedCity]);

  const label = selectedCity?.name || (loading ? "Loading…" : "Select your city");
  const dotColor = selectedCity ? "bg-green-500" : "bg-amber-500";

  return (
    <header
      className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200"
      data-testid="carservice-header"
    >
      <div className={`${classes.container} flex items-center justify-between h-16 sm:h-20`}>
        <div className="flex items-center gap-3 sm:gap-5">
          <a href="/carservice" className="flex items-center" aria-label="Wisedrive Car Service home">
            <img
              src="/wisedrive-logo-lp.png"
              alt="Wisedrive"
              className="h-8 sm:h-10 w-auto"
            />
          </a>
          {/* City dropdown — data-driven from /api/public/carservice/cities */}
          <div ref={dropdownRef} className="relative">
            <button
              ref={triggerRef}
              type="button"
              data-testid="header-city-pill"
              onClick={() => {
                setOpen((v) => !v);
                setActiveIdx(-1);
              }}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors text-xs font-bold text-slate-700 disabled:opacity-60"
              title={selectedCity ? `Servicing ${selectedCity.name}` : "Choose your city"}
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls="header-city-listbox"
              aria-activedescendant={
                open && activeIdx >= 0 && cities[activeIdx]
                  ? `header-city-opt-${cities[activeIdx].id}`
                  : undefined
              }
              aria-label="Choose city"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
              <span className="hidden xs:inline">
                <MapPin className="h-3 w-3 inline mr-1 -mt-0.5" />
              </span>
              <span className="whitespace-nowrap">{label}</span>
              <ChevronDown className={`h-3 w-3 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <ul
                id="header-city-listbox"
                data-testid="header-city-dropdown"
                role="listbox"
                className="absolute left-0 mt-2 w-52 max-h-72 overflow-auto rounded-lg bg-white shadow-lg border border-slate-200 py-1 z-50"
              >
                {cities.length === 0 && !loading && (
                  <li className="px-3 py-2 text-xs text-slate-500">
                    No cities available
                  </li>
                )}
                {cities.map((city, idx) => {
                  const isActive = selectedCity?.id === city.id;
                  const isKbHover = activeIdx === idx;
                  return (
                    <li key={city.id}>
                      <button
                        type="button"
                        role="option"
                        id={`header-city-opt-${city.id}`}
                        aria-selected={isActive}
                        data-testid={`header-city-option-${city.name.toLowerCase().replace(/\s+/g, "-")}`}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => {
                          setSelectedCity(city);
                          setOpen(false);
                          triggerRef.current?.focus();
                          trackEvent("city_selected", { city: city.name, source: "header_dropdown" });
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          isActive ? "font-bold text-slate-900 bg-slate-50" : "text-slate-700"
                        } ${isKbHover ? "bg-slate-100" : "hover:bg-slate-100"}`}
                      >
                        {city.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={telHref}
            onClick={() => {
              trackEvent("call_clicked", { location: "header" });
            }}
            data-testid="header-call-cta"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-all text-sm font-semibold text-slate-900"
          >
            <Phone className="h-4 w-4" />
            <span className="font-mono">{PHONE_NUMBER_DISPLAY}</span>
          </a>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent("whatsapp_clicked", { location: "header" });
            }}
            data-testid="header-whatsapp-cta"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-green-600 hover:bg-green-700 transition-all text-sm font-semibold text-white shadow-sm hover:shadow-md hover:-translate-y-[1px]"
          >
            <MessageCircle className="h-4 w-4" />
            <span className="hidden sm:inline">WhatsApp Us</span>
            <span className="sm:hidden">Chat</span>
          </a>
        </div>
      </div>
    </header>
  );
};

export default CarServiceHeader;
